import React, { useMemo } from 'react';
import { Typography, Tag, Table, Button, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined } from '@ant-design/icons';
import { useAuth } from '../../components/Layout/AuthContext';
import PageList from '../../components/Templates/PageList';
import type { FieldConfig } from '../../components/ForgeQueryBuilder';
import { exportTaskApi } from '../../services/manager/export-task';
import { ExportTask, ExportStatus, ExportStatusMap, ExportStatusColorMap } from '../../types/entity/export-task';
import { useStaffMap } from '../../services/store/organization.store';
import dayjs from 'dayjs';

const { Text } = Typography;

const ExportTasks: React.FC = () => {
  const { staffMap } = useStaffMap();
  const { staff } = useAuth();

  const queryFields: FieldConfig[] = useMemo(() => [
    { 
      key: 'task_name', 
      label: '任务名称', 
      type: 'string', 
      operators: ['like'], 
      trim: true 
    },
    { 
      key: 'status', 
      label: '状态', 
      type: 'select', 
      operators: ['eq'],
      options: Object.entries(ExportStatusMap).map(([value, label]) => ({ 
        label, 
        value: Number(value) 
      }))
    },
    { 
      key: 'created_at', 
      label: '创建时间', 
      type: 'date', 
      operators: ['between'], 
      format: 'YYYY-MM-DD', 
      showTime: true,
      placeholder: ['开始时间', '结束时间'] 
    },
  ], []);

  const defaultQuery = useMemo(() => {
    const query: any = {
      sort: [{ field: 'id', order: 'desc' as const }]
    };
    if (staff?.id) {
      query.filter = { creator_id: staff.id };
    }
    return query;
  }, [staff?.id]);

  const handleDownload = (record: ExportTask) => {
    if (!record.fileUrl) return;

    fetch(record.fileUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        // 获取文件后缀
        const ext = record.fileName?.split('.').pop() || 'xlsx';
        a.download = `${record.taskName}.${ext}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch((error) => {
        console.error('Download failed:', error);
        // 降级处理：直接打开
        window.open(record.fileUrl, '_blank');
      });
  };

  const columns: ColumnsType<ExportTask> = [
    {
      title: 'ID',
      key: 'id',
      width: 80,
      dataIndex: 'id',
    },
    {
      title: '任务名称',
      key: 'taskName',
      width: 200,
      render: (_, rec) => <Text>{rec.taskName}</Text>,
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_, rec) => (
        <Tooltip title={rec.errorMessage}>
          <Tag color={ExportStatusColorMap[rec.status]}>{ExportStatusMap[rec.status]}</Tag>
        </Tooltip>
      ),
    },
    {
      title: '文件名',
      key: 'fileName',
      width: 200,
      render: (_, rec) => <Text>{rec.fileName || '-'}</Text>,
    },
    {
      title: '数据量',
      key: 'recordCount',
      width: 100,
      render: (_, rec) => <Text>{rec.recordCount || '-'}</Text>,
    },
    {
      title: '创建人',
      key: 'creatorId',
      width: 120,
      render: (_, rec) => {
        const staff = staffMap.get(rec.creatorId);
        return <Text>{staff ? staff.name : rec.creatorId}</Text>;
      },
    },
    {
      title: '创建时间',
      key: 'createdAt',
      width: 180,
      render: (_, rec) => <Text>{dayjs(rec.createdAt).format('YYYY-MM-DD HH:mm:ss')}</Text>,
    },
    {
      title: '完成时间',
      key: 'finishedAt',
      width: 180,
      render: (_, rec) => <Text>{rec.finishedAt ? dayjs(rec.finishedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}</Text>,
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, rec) => (
        rec.status === ExportStatus.Completed && rec.fileUrl ? (
          <Button 
            type="link" 
            icon={<DownloadOutlined />} 
            onClick={() => handleDownload(rec)}
          >
            下载
          </Button>
        ) : null
      ),
    },
  ];

  return (
    <PageList<ExportTask>
      title="导出任务管理"
      queryFields={queryFields}
      defaultQuery={defaultQuery}
      fetcher={async (params) => {
        const mergedParams = {
          ...params,
          filter: {
            ...defaultQuery.filter,
            ...(params.filter || {})
          },
          sort: (params.sort && params.sort.length > 0) ? params.sort : defaultQuery.sort
        };
        const res = await exportTaskApi.list(mergedParams);
        return {
          rows: res.data.items,
          pagination: res.data.pagination,
        };
      }}
      renderList={({ rows, loading }) => (
        <div style={{ background: '#fff' }}>
           <Table
             loading={loading}
             dataSource={rows}
             columns={columns}
             rowKey="id"
             pagination={false}
             scroll={{ x: 1300 }}
           />
        </div>
      )}
    />
  );
};

export default ExportTasks;
